import { AuthContext } from './definition'
import { getSession } from '../../lib/session/getSession'
import { GoogleSession, OneLoginSession } from '../../types'
import { logout as sessionLogout } from '../../lib/session/logout'
import { login as sessionLogin } from '../../lib/session/login'
import sendRebuttalEvent from '../../lib/api/middleware/sendRebuttalEvent'
import {
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useMemo,
  useContext,
} from 'react'
import { isAxiosError } from 'axios'
import {
  destroyToken,
  getMe,
  postSsoToken,
} from '../../lib/api/moduleManager/auth'
import { SettingsContext } from '../SettingsContext/definition'

const LOGIN_EXPIRY_MIN = 1440 // 24 hours
const LOGIN_EXPIRY_MS = LOGIN_EXPIRY_MIN * 60 * 1000
const MM_TOKEN_KEY = 'TD-ModuleManagerToken'
const MM_TOKEN_TYPE_KEY = 'TD-ModuleManagerTokenType'
const MM_PERMISSIONS_KEY = 'TD-ModuleManagerPermissions'

function getStoredPermissions(): string[] {
  try {
    const raw = localStorage.getItem(MM_PERMISSIONS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === 'string')
      : []
  } catch {
    return []
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { updateLocation } = useContext(SettingsContext)

  const [contextSession, setContextSession] = useState<
    GoogleSession | OneLoginSession | null
  >(getSession())
  const [role, setRole] = useState<string | null>(
    localStorage.getItem('TD-AgentRole') || null,
  )
  const [permissions, setPermissions] = useState<string[]>(getStoredPermissions)
  const [moduleManagerToken, setModuleManagerToken] = useState<string | null>(
    localStorage.getItem(MM_TOKEN_KEY),
  )
  const [moduleManagerTokenType, setModuleManagerTokenType] = useState<string>(
    localStorage.getItem(MM_TOKEN_TYPE_KEY) || 'Bearer',
  )

  const [isLoaded, setIsLoaded] = useState(false)

  const clearModuleManagerAuth = useCallback(() => {
    setPermissions([])
    setModuleManagerToken(null)
    setModuleManagerTokenType('Bearer')
    localStorage.removeItem(MM_TOKEN_KEY)
    localStorage.removeItem(MM_TOKEN_TYPE_KEY)
    localStorage.removeItem(MM_PERMISSIONS_KEY)
  }, [])

  const fetchAndStoreModuleManagerAuth = useCallback(async (ssoJwt: string) => {
    try {
      const tokenData = await postSsoToken(ssoJwt)
      const nextTokenType = tokenData.token_type || 'Bearer'

      localStorage.setItem(MM_TOKEN_KEY, tokenData.token)
      localStorage.setItem(MM_TOKEN_TYPE_KEY, nextTokenType)
      setModuleManagerToken(tokenData.token)
      setModuleManagerTokenType(nextTokenType)

      const me = await getMe(tokenData.token, nextTokenType)

      if (Object.keys(me).length === 0) {
        window.location.href = '/error' // we didn't get a successful "/me" call, so redirect to error and call mattermost
      }

      const user = tokenData.user ?? me
      const nextPermissions = user.permissions.map(
        (permission) => permission.name,
      )

      updateLocation(user.office_location, true)

      localStorage.setItem(MM_PERMISSIONS_KEY, JSON.stringify(nextPermissions))
      setPermissions(nextPermissions)
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 422) {
        console.warn(
          '[AuthContext] Invalid SSO token (422), clearing credentials',
        )
        localStorage.removeItem('td-credential')
      }
      throw error
    }
  }, [])

  const login = async (
    sessionData: GoogleSession | OneLoginSession,
    userRole: string | null,
  ) => {
    setContextSession(sessionData)
    sessionLogin(sessionData)
    setRole(userRole)

    if (userRole) {
      localStorage.setItem('TD-AgentRole', userRole)
    } else {
      localStorage.removeItem('TD-AgentRole')
    }

    const ssoJwt = localStorage.getItem('td-credential')
    if (!ssoJwt) {
      clearModuleManagerAuth()
      return
    }

    try {
      await fetchAndStoreModuleManagerAuth(ssoJwt)
    } catch (error) {
      console.error(
        '[AuthContext] Failed to exchange SSO token for module-manager auth:',
        error,
      )
      clearModuleManagerAuth()
      throw error
    }
  }

  const logout = useCallback(async () => {
    if (moduleManagerToken) {
      try {
        await destroyToken(moduleManagerToken, moduleManagerTokenType)
      } catch (error) {
        console.error(
          '[AuthContext] Failed to destroy module-manager token:',
          error,
        )
      }
    }

    sendRebuttalEvent({
      debtAmount: 0,
      eventType: 'user-logout',
      eventMessage: {
        email_verified: contextSession?.email_verified || '',
        name: contextSession?.name || '',
      },
      userEmail: contextSession?.email || '',
    })

    setContextSession(null)
    setRole(null)
    clearModuleManagerAuth()
    sessionLogout()
  }, [
    contextSession?.email,
    contextSession?.email_verified,
    contextSession?.name,
    moduleManagerToken,
    moduleManagerTokenType,
    clearModuleManagerAuth,
  ])

  useEffect(() => {
    const session = getSession()
    if (!session) {
      clearModuleManagerAuth()
      setIsLoaded(true)
      return
    }

    const token = localStorage.getItem(MM_TOKEN_KEY)
    const tokenType = localStorage.getItem(MM_TOKEN_TYPE_KEY) || 'Bearer'
    const ssoJwt = localStorage.getItem('td-credential')

    const restore = async () => {
      try {
        if (token) {
          setModuleManagerToken(token)
          setModuleManagerTokenType(tokenType)
          const user = await getMe(token, tokenType)
          localStorage.setItem('TD-user_ID', user.id.toString())
          const nextPermissions = user.permissions.map(
            (permission) => permission.name,
          )
          localStorage.setItem(
            MM_PERMISSIONS_KEY,
            JSON.stringify(nextPermissions),
          )
          setPermissions(nextPermissions)
        } else if (ssoJwt) {
          await fetchAndStoreModuleManagerAuth(ssoJwt)
        }
      } catch (error) {
        console.error(
          '[AuthContext] Failed to restore module-manager auth:',
          error,
        )
        clearModuleManagerAuth()
      } finally {
        setIsLoaded(true)
      }
    }

    restore()
  }, [clearModuleManagerAuth, fetchAndStoreModuleManagerAuth])

  useEffect(() => {
    const session = getSession()

    if (session) {
      const elapsed = Date.now() - session.loggedInAt
      if (elapsed > LOGIN_EXPIRY_MS) {
        logout()
      } else {
        const timeout = setTimeout(() => {
          logout()
        }, LOGIN_EXPIRY_MS - elapsed)
        return () => clearTimeout(timeout)
      }
    }
  }, [logout])

  const hasPermission = useCallback(
    (permission: string) => {
      if (permissions.includes(permission)) return true
      if (!role) return false
      if (role === '*') return true
      return role.split(',').includes(permission)
    },
    [permissions, role],
  )

  const getToken = useCallback(() => {
    return moduleManagerToken || localStorage.getItem(MM_TOKEN_KEY)
  }, [moduleManagerToken])

  const contextValue = useMemo(
    () => ({
      role,
      session: contextSession,
      permissions,
      moduleManagerToken,
      moduleManagerTokenType,
      login,
      logout,
      hasPermission,
      getToken,
      isLoaded,
    }),
    [
      role,
      contextSession,
      permissions,
      moduleManagerToken,
      moduleManagerTokenType,
      login,
      logout,
      hasPermission,
      getToken,
      isLoaded,
    ],
  )

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}
